/**
 * Task & Action Management — data store.
 *
 * Two backends behind one interface (see `actionsStore` below):
 *   - API mode (VITE_DATA_SOURCE=api): talks to the real backend at
 *     `/action-tasks` (see the API's app/api/action_tasks.py).
 *   - Mock mode: an in-browser store on its OWN localStorage key + seed. It
 *     does not touch the application's shared mock DB (`services/mock/db.ts`),
 *     so removing this module leaves no orphaned data or migrations behind.
 */
import { httpClient } from '@/services/http/client'
import { USING_API } from '@/services/config'
import { CATEGORIES, CATEGORY_MAP, OFFICE_STAFF } from './constants'
import type { ActionTask, ActionPriority, ActionStatus } from './types'

const STORAGE_KEY = 'mcsms.actions.v3'

/** The Personal Secretary — the primary operator of this module. */
export const CURRENT_USER_ID = 'staff-ps'

// ---------------------------------------------------------------------------
// Persistence
// ---------------------------------------------------------------------------
let cache: ActionTask[] | null = null

function load(): ActionTask[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) return JSON.parse(raw) as ActionTask[]
  } catch {
    /* ignore corrupt storage */
  }
  const seeded = buildSeed()
  persist(seeded)
  return seeded
}

function persist(next: ActionTask[]) {
  cache = next
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch {
    /* storage full — keep in-memory only */
  }
}

function db(): ActionTask[] {
  if (!cache) cache = load()
  return cache
}

/** Wipe and re-seed — handy during development. */
export function resetActions() {
  localStorage.removeItem(STORAGE_KEY)
  cache = buildSeed()
  persist(cache)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
let seq = 0
function uid(prefix: string): string {
  seq += 1
  return `${prefix}-${Date.now().toString(36)}-${seq}-${Math.floor(Math.random() * 1e4)}`
}

function nextNumber(list: ActionTask[]): string {
  const max = list.reduce((m, t) => {
    const n = Number(t.number.replace(/\D/g, ''))
    return Number.isNaN(n) ? m : Math.max(m, n)
  }, 0)
  return `ACT-${String(max + 1).padStart(4, '0')}`
}

async function latency<T>(fn: () => T): Promise<T> {
  await new Promise((r) => setTimeout(r, 120 + Math.random() * 200))
  return fn()
}

// ---------------------------------------------------------------------------
// API — in-browser mock (VITE_DATA_SOURCE=mock) + real backend (=api) below
// ---------------------------------------------------------------------------
const mockActionsStore = {
  all: (): Promise<ActionTask[]> =>
    latency(() => [...db()].sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))),

  get: (id: string): Promise<ActionTask | undefined> => latency(() => db().find((t) => t.id === id)),

  create: (input: Partial<ActionTask>): Promise<ActionTask> =>
    latency(() => {
      const list = db()
      const now = new Date().toISOString()
      const task: ActionTask = {
        id: uid('act'),
        number: nextNumber(list),
        title: input.title ?? 'Untitled task',
        description: input.description,
        category: input.category ?? 'make_call',
        priority: input.priority ?? 'medium',
        status: input.status ?? 'not_started',
        createdById: input.createdById ?? CURRENT_USER_ID,
        assignedById: input.assignedById ?? CURRENT_USER_ID,
        assignedToId: input.assignedToId,
        createdAt: now,
        startDate: input.startDate,
        endDate: input.endDate,
        reminderAt: input.reminderAt,
        estimatedMinutes: input.estimatedMinutes,
        completedAt: input.status === 'completed' ? now : undefined,
        checklist: input.checklist ?? [],
        dependencyIds: input.dependencyIds ?? [],
        notes: input.notes ?? [],
        related: input.related,
        ministerQueue: input.ministerQueue ?? false,
        ministerOutcome: input.ministerOutcome,
        tags: input.tags ?? [],
        updatedAt: now,
      }
      persist([task, ...list])
      return task
    }),

  update: (id: string, patch: Partial<ActionTask>): Promise<ActionTask> =>
    latency(() => {
      const list = db()
      const now = new Date().toISOString()
      let updated: ActionTask | undefined
      const next = list.map((t) => {
        if (t.id !== id) return t
        updated = {
          ...t,
          ...patch,
          // Stamp / clear the completion time as status changes.
          completedAt:
            patch.status === 'completed'
              ? t.completedAt ?? now
              : patch.status && patch.status !== 'completed'
                ? undefined
                : t.completedAt,
          updatedAt: now,
        }
        return updated
      })
      persist(next)
      if (!updated) throw new Error('Action not found')
      return updated
    }),

  remove: (id: string): Promise<{ id: string }> =>
    latency(() => {
      persist(db().filter((t) => t.id !== id))
      return { id }
    }),
}

// ---------------------------------------------------------------------------
// Real backend implementation (VITE_DATA_SOURCE=api). The backend uses numeric
// ids and generates the number + completedAt; we convert at the boundary.
// ---------------------------------------------------------------------------
const fromApi = (raw: any): ActionTask => ({
  id: String(raw.id),
  number: raw.number ?? '',
  title: raw.title,
  description: raw.description ?? undefined,
  category: raw.category ?? 'make_call',
  priority: raw.priority,
  status: raw.status,
  createdById: raw.createdById ?? CURRENT_USER_ID,
  assignedById: raw.assignedById ?? undefined,
  assignedToId: raw.assignedToId ?? undefined,
  createdAt: raw.createdAt,
  startDate: raw.startDate ?? undefined,
  endDate: raw.endDate ?? undefined,
  reminderAt: raw.reminderAt ?? undefined,
  estimatedMinutes: raw.estimatedMinutes ?? undefined,
  completedAt: raw.completedAt ?? undefined,
  checklist: raw.checklist ?? [],
  dependencyIds: (raw.dependencyIds ?? []).map((id: number) => String(id)),
  notes: raw.notes ?? [],
  related: raw.related ?? undefined,
  ministerQueue: raw.ministerQueue ?? false,
  ministerOutcome: raw.ministerOutcome ?? undefined,
  tags: (raw.tags ?? []).map((t: any) => ({ id: String(t.id), label: t.label, color: t.color ?? 'slate' })),
  updatedAt: raw.updatedAt,
})

// Build the API body; only send writable, present keys (the backend owns
// number/completedAt/createdAt/updatedAt).
const toApi = (p: Partial<ActionTask>): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  const has = (k: keyof ActionTask) => k in p
  if (has('title')) out.title = p.title
  if (has('description')) out.description = p.description ?? null
  if (has('category')) out.category = p.category ?? null
  if (has('priority')) out.priority = p.priority
  if (has('status')) out.status = p.status
  if (has('createdById')) out.createdById = p.createdById ?? null
  if (has('assignedById')) out.assignedById = p.assignedById ?? null
  if (has('assignedToId')) out.assignedToId = p.assignedToId ?? null
  if (has('startDate')) out.startDate = p.startDate ?? null
  if (has('endDate')) out.endDate = p.endDate ?? null
  if (has('reminderAt')) out.reminderAt = p.reminderAt ?? null
  if (has('estimatedMinutes')) out.estimatedMinutes = p.estimatedMinutes ?? null
  if (has('ministerQueue')) out.ministerQueue = p.ministerQueue
  if (has('ministerOutcome')) out.ministerOutcome = p.ministerOutcome ?? null
  if (has('checklist')) out.checklist = p.checklist
  if (has('notes')) out.notes = p.notes
  if (has('dependencyIds')) out.dependencyIds = p.dependencyIds?.map((id) => Number(id))
  if (has('related')) out.related = p.related ?? null
  if (has('tags')) out.tags = p.tags ?? []
  return out
}

const httpActionsStore = {
  all: async (): Promise<ActionTask[]> => (await httpClient.get<any[]>('/action-tasks')).data.map(fromApi),

  get: async (id: string): Promise<ActionTask | undefined> =>
    fromApi((await httpClient.get('/action-tasks/' + id)).data),

  create: async (input: Partial<ActionTask>): Promise<ActionTask> =>
    fromApi((await httpClient.post('/action-tasks', toApi(input))).data),

  update: async (id: string, patch: Partial<ActionTask>): Promise<ActionTask> =>
    fromApi((await httpClient.patch('/action-tasks/' + id, toApi(patch))).data),

  remove: async (id: string): Promise<{ id: string }> => {
    await httpClient.delete('/action-tasks/' + id)
    return { id }
  },
}

/** Pick the backend: HTTP in API mode, the in-browser store otherwise. */
export const actionsStore = USING_API ? httpActionsStore : mockActionsStore

// ---------------------------------------------------------------------------
// Seed — a realistic day in the Minister's office
// ---------------------------------------------------------------------------
function buildSeed(): ActionTask[] {
  const now = Date.now()
  const day = 86_400_000
  const iso = (offsetMs: number) => new Date(now + offsetMs).toISOString()
  const pick = <T>(arr: readonly T[]): T => arr[Math.floor(Math.random() * arr.length)]

  type Spec = {
    title: string
    category: string
    priority: ActionPriority
    status: ActionStatus
    due?: number // days offset
    minister?: boolean
    checklist?: string[]
    related?: ActionTask['related']
    est?: number
  }

  const specs: Spec[] = [
    { title: 'Remind Minister to call the Chief Secretary', category: 'remind_minister', priority: 'critical', status: 'not_started', due: 0, minister: true, est: 5 },
    { title: 'Return missed call — District Magistrate, Nadia', category: 'return_call', priority: 'high', status: 'not_started', due: 0, minister: true, est: 6 },
    { title: 'Obtain Minister signature on pending water sanction file', category: 'obtain_signature', priority: 'high', status: 'waiting', due: 0, minister: true, est: 4 },
    { title: 'Prepare briefing note for foreign delegation meeting', category: 'prepare_briefing', priority: 'high', status: 'in_progress', due: 1, est: 30, checklist: ['Collect department inputs', 'Draft summary', 'Add talking points', 'Print 3 copies'], related: { type: 'meeting', id: '', label: 'Foreign Delegation — Investment', to: '/meetings' } },
    { title: 'Arrange refreshments for District Review Meeting', category: 'arrange_refreshments', priority: 'medium', status: 'assigned', due: 1, est: 15, related: { type: 'meeting', id: '', label: 'District Review Meeting', to: '/meetings' }, checklist: ['Confirm headcount', 'Order tea & snacks', 'Confirm timing'] },
    { title: 'Send congratulations to newly appointed Health Secretary', category: 'send_congratulations', priority: 'medium', status: 'not_started', due: 2, minister: true, est: 5 },
    { title: 'Follow-up with PWD on road project approval', category: 'followup_department', priority: 'high', status: 'waiting', due: -1, est: 10, related: { type: 'department', id: '', label: 'Public Works Department', to: '/departments' } },
    { title: 'Book travel for constituency visit', category: 'book_travel', priority: 'medium', status: 'in_progress', due: 3, est: 20, checklist: ['Confirm dates', 'Arrange vehicle', 'Inform security', 'Book accommodation'] },
    { title: 'Review proposal summary before evening review', category: 'review_file', priority: 'high', status: 'not_started', due: 0, minister: true, est: 8 },
    { title: 'Send condolence message to family of veteran leader', category: 'send_condolence', priority: 'high', status: 'not_started', due: 0, minister: true, est: 5 },
    { title: 'Arrange vehicle for site inspection', category: 'arrange_vehicle', priority: 'medium', status: 'assigned', due: 2, est: 15 },
    { title: 'Dispatch invitation letters for inauguration', category: 'dispatch_documents', priority: 'medium', status: 'on_hold', due: 4, est: 10 },
    { title: 'Confirm attendance for cabinet coordination meeting', category: 'confirm_attendance', priority: 'medium', status: 'not_started', due: 1, est: 10 },
    { title: 'Coordinate protocol for Governor visit', category: 'coordinate_protocol', priority: 'critical', status: 'in_progress', due: 2, est: 30, checklist: ['Confirm route', 'Brief security', 'Arrange reception'] },
    { title: 'Collect monthly progress report from Rural Development', category: 'collect_report', priority: 'low', status: 'not_started', due: 5, est: 10 },
    { title: 'Return two missed calls from morning', category: 'return_call', priority: 'medium', status: 'not_started', due: 0, minister: true, est: 7 },
    { title: 'Arrange flowers for felicitation event', category: 'arrange_flowers', priority: 'low', status: 'assigned', due: 3, est: 10 },
    { title: 'Print documents for signature folder', category: 'print_documents', priority: 'medium', status: 'not_started', due: 0, est: 10 },
    { title: 'Follow-up call — Education Secretary on scholarship scheme', category: 'followup_call', priority: 'medium', status: 'not_started', due: 1, minister: true, est: 5 },
    { title: 'Submit compliance report to Chief Secretary office', category: 'submit_report', priority: 'high', status: 'completed', due: -2, est: 10 },
    { title: 'Purchase stationery for office', category: 'purchase_items', priority: 'low', status: 'completed', due: -3, est: 20 },
    { title: 'Prepare presentation for investment summit', category: 'prepare_presentation', priority: 'high', status: 'in_progress', due: 6, est: 45, checklist: ['Gather data', 'Design slides', 'Review with team'] },
    { title: 'Arrange video conference with district officials', category: 'arrange_vc', priority: 'medium', status: 'not_started', due: 2, est: 15 },
    { title: 'Remind Minister about evening felicitation event', category: 'remind_minister', priority: 'high', status: 'not_started', due: 0, minister: true, est: 5 },
  ]

  const staff = OFFICE_STAFF.map((s) => s.id)

  return specs.map((s, i) => {
    const created = iso(-((i % 6) + 1) * day - Math.floor(Math.random() * day))
    const cat = CATEGORY_MAP[s.category]
    const done = s.status === 'completed'
    return {
      id: `act-seed-${String(i + 1).padStart(3, '0')}`,
      number: `ACT-${String(i + 1).padStart(4, '0')}`,
      title: s.title,
      description: undefined,
      category: s.category,
      priority: s.priority,
      status: s.status,
      createdById: CURRENT_USER_ID,
      assignedById: CURRENT_USER_ID,
      assignedToId: pick(staff),
      createdAt: created,
      // `due` is the scheduled day → start date. A past-due open task also gets
      // an end date (deadline) in the past so it reads as overdue.
      startDate: s.due !== undefined ? iso(s.due * day + 9 * 3_600_000) : undefined,
      endDate: s.due !== undefined && s.due < 0 ? iso(s.due * day + 18 * 3_600_000) : undefined,
      reminderAt: s.due !== undefined ? iso(s.due * day + 9 * 3_600_000) : undefined,
      estimatedMinutes: s.est ?? cat?.minutes,
      completedAt: done ? iso(-1 * day) : undefined,
      checklist: (s.checklist ?? []).map((text, k) => ({ id: `chk-${i}-${k}`, text, done: done || (k === 0 && s.status === 'in_progress') })),
      dependencyIds: [],
      notes: [],
      related: s.related,
      ministerQueue: !!s.minister,
      ministerOutcome: undefined,
      updatedAt: created,
    }
  })
}

/** Deterministic category list for pickers (re-export for convenience). */
export { CATEGORIES }
